/**
 * Invisible text + link layer for raster PDF exports.
 *
 * The visual comes from an html2canvas screenshot, which carries no text. This walks the
 * same DOM and writes every word into the PDF with `renderingMode: "invisible"` at the
 * position it occupies on screen, plus a link annotation over every anchor. The result
 * looks identical to the image but is selectable, searchable and clickable.
 */

import type { jsPDF } from "jspdf";

export interface PdfMargin {
  left: number;
  top: number;
}

interface Point {
  left: number;
  top: number;
}

export interface TextItem {
  text: string;
  x: number;
  y: number;
  fontSize: number;
}

export interface LinkItem {
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Share of the font size that sits above the baseline for the Latin faces we ship.
const ASCENT_RATIO = 0.8;

const WORD = /\S+/g;

const LINKABLE_PROTOCOLS = ["http:", "https:", "mailto:"];

// The built-in jsPDF fonts are WinAnsi only, so anything above latin-1 needs a TTF.
const LATIN1_MAX = 0xff;
const UNICODE_FONT_FILE = "roboto-regular.ttf";
const UNICODE_FONT_ID = "Roboto";

/**
 * Baseline offset from the top of a line box. A line box is taller than its font when
 * line-height > 1, and the surplus is split evenly above and below (half-leading).
 */
export const baselineOffset = (lineBoxHeight: number, fontSize: number) =>
  Math.max(lineBoxHeight - fontSize, 0) / 2 + fontSize * ASCENT_RATIO;

/** Viewport rect to PDF point, relative to the captured root. CSS px are points 1:1. */
export const rectToPdfPoint = (
  rect: Point,
  rootRect: Point,
  margin: PdfMargin
) => ({
  x: margin.left + rect.left - rootRect.left,
  y: margin.top + rect.top - rootRect.top
});

/** Link annotations need an absolute url. Returns undefined for non-navigable hrefs. */
export const toAbsoluteUrl = (href: string): string | undefined => {
  if (!href || href.startsWith("#")) return undefined;

  try {
    const url = new URL(href, document.baseURI);

    return LINKABLE_PROTOCOLS.includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const textNodes = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Node[] = [];
  let node = walker.nextNode();

  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }

  return nodes;
};

/**
 * One run per word: positions stay aligned with the captured image even where text wraps,
 * and words hidden by display:none or clipping report empty rects and drop out.
 */
const wordItems = (
  node: Node,
  rootRect: DOMRect,
  margin: PdfMargin
): TextItem[] => {
  const value = node.nodeValue ?? "";
  const parent = node.parentElement;

  if (!value.trim() || !parent) return [];

  const style = window.getComputedStyle(parent);
  const fontSize = parseFloat(style.fontSize);

  if (style.visibility === "hidden" || !fontSize) return [];

  const range = document.createRange();

  return Array.from(value.matchAll(WORD))
    .map(match => {
      const start = match.index ?? 0;
      range.setStart(node, start);
      range.setEnd(node, start + match[0].length);

      return { text: match[0], rect: range.getBoundingClientRect() };
    })
    .filter(({ rect }) => rect.width && rect.height)
    .map(({ text, rect }) => {
      const { x, y } = rectToPdfPoint(rect, rootRect, margin);

      return {
        text,
        x,
        y: y + baselineOffset(rect.height, fontSize),
        fontSize
      };
    });
};

const linkItems = (
  anchor: Element,
  rootRect: DOMRect,
  margin: PdfMargin
): LinkItem[] => {
  const url = toAbsoluteUrl(anchor.getAttribute("href") ?? "");

  if (!url) return [];

  return Array.from(anchor.getClientRects())
    .filter(rect => rect.width && rect.height)
    .map(rect => ({
      url,
      ...rectToPdfPoint(rect, rootRect, margin),
      width: rect.width,
      height: rect.height
    }));
};

const hasNonLatin1 = (text: string) =>
  Array.from(text).some(char => (char.codePointAt(0) ?? 0) > LATIN1_MAX);

const toBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return window.btoa(binary);
};

/**
 * Registers Roboto so non-latin text survives into the layer. Glyph looks are irrelevant
 * here (the text is invisible), only the characters a reader can extract matter.
 */
const registerUnicodeFont = async (pdf: jsPDF) => {
  const response = await fetch(
    `${window.tcbase.urls.static}fonts/${UNICODE_FONT_FILE}`
  );
  pdf.addFileToVFS(UNICODE_FONT_FILE, toBase64(await response.arrayBuffer()));
  pdf.addFont(UNICODE_FONT_FILE, UNICODE_FONT_ID, "normal");
  pdf.setFont(UNICODE_FONT_ID);
};

export const addTextLayer = async (
  pdf: jsPDF,
  root: HTMLElement,
  margin: PdfMargin
) => {
  const rootRect = root.getBoundingClientRect();
  const items = textNodes(root).flatMap(node =>
    wordItems(node, rootRect, margin)
  );

  if (items.some(item => hasNonLatin1(item.text))) {
    try {
      await registerUnicodeFont(pdf);
    } catch (error) {
      // Keep the export going with the built-in font rather than failing the download.
      console.warn("PDF text layer falling back to the built-in font", error);
    }
  }

  items.forEach(item => {
    pdf.setFontSize(item.fontSize);
    pdf.text(item.text, item.x, item.y, { renderingMode: "invisible" });
  });

  Array.from(root.querySelectorAll("a[href]"))
    .flatMap(anchor => linkItems(anchor, rootRect, margin))
    .forEach(link => {
      pdf.link(link.x, link.y, link.width, link.height, { url: link.url });
    });
};
